import { ConfigIngestModes, Permission } from './recordingTypes';

export type OrganizationType = 'academic' | 'corporate';
export type HostType = 'instructor' | 'teacher' | 'professor' | 'host' | 'faculty' | 'producer' | 'facilitator';
export type AssistantType =
    | 'assistant'
    | 'faculty'
    | 'co_host'
    | 'adj_faculty'
    | 'adj_facilitator'
    | 'planner'
    | 'producer';
export type StudentType = 'student' | 'participant' | 'learner';
export type RosterType = 'roster' | 'roll' | 'participants';
export type GradebookType = 'gradebook' | 'reports' | 'results' | 'outcomes' | 'feedback' | 'completions' | 'credits';
export type ProctoringType = 'proctoring_view' | 'collaboration_view';
export type DocumentType =
    | 'syllabus'
    | 'rubric'
    | 'outline'
    | 'agenda'
    | 'schedule'
    | 'overview'
    | 'curriculum'
    | 'program';
export type ZoomAppId = 'user' | 'collab' | 'teams';

export interface TerminologySettings {
    hostTitle: HostType;
    assistantsTitle: AssistantType;
    studentsTitle: StudentType;
    rosterTitle: RosterType;
    gradebookTitle: GradebookType;
    documentTitle: DocumentType;
    proctoringTitle: ProctoringType;
}

export enum UnauthorizedUserAccess {
    Allow = 'allow',
    Deny = 'deny',
    SignIn = 'signin'
}

export enum ClassAccess {
    All = 'all',
    App = 'app',
    Lti = 'lti',
    None = 'none'
}

export enum WebExpOptions {
    Classic = 'classic',
    NextGen = 'nextgen'
}

export type ClassSettingsOptions = {
    webExperience?: WebExpOptions;
    unauth_user_access?: UnauthorizedUserAccess;
    unauth_user_text?: string | null;
    allowVanillaZoom?: boolean;
};

export enum ClassLevelOverrideOptions {
    NoOne = 'no one',
    AdminsOnly = 'admins only',
    AdminsAndInstructors = 'admins and instructors'
}

export enum UserAccountCreationOptions {
    AdminOnly = 'admin',
    Domains = 'domains',
    AllUsers = 'all'
}

export type SchoolSettingsOptions = {
    webExperience?: WebExpOptions;
    webExperienceOverride?: ClassLevelOverrideOptions;

    authenticationOverride?: ClassLevelOverrideOptions;

    allowVanillaZoom?: boolean;
    allowVanillaZoomOverride?: ClassLevelOverrideOptions;

    forceWebAsChromebook?: boolean;
    azureTenantIds?: string[];
    userAccountCreation?: UserAccountCreationOptions;
    userAccountCreationDomains?: string[];
};

export interface FeatureSettings {
    classDocument: boolean;
    classGradebook: boolean;
    classAssessments: boolean;
    lms: boolean;
    surveys: boolean;
    polling: boolean;
    proctoring: boolean;
    enableLogAutoUpload: boolean;
    hasPanicMode: boolean;
    hasShareFiles: boolean;
    whiteboard: boolean;
    assignments: boolean;
    chat: boolean;
    loseFocusTracking: boolean;
    webApp: boolean;
    forceWebAppChromebooks: boolean;
    allowZoom: boolean;
    allowApp: boolean;
    autoTranscriptions: boolean;
    classNotes: boolean;
    sendInviteEmails: boolean;
    showMeetingInfo: boolean;
    promptTeachersForFeedback: boolean;
    promptStudentsForFeedback: boolean;
    notifyOutdatedClassVersion: boolean;
    enforceClassMinimumVersion: boolean;
    recordingLegacyZoomList: boolean;
    recordingInitialPermission: Permission;
    recordingIngestMode: ConfigIngestModes;
    recordingParticipantsCanView: boolean;
    recordingRestrictGallery: boolean;
    recordingTabAccess: ClassAccess;
    attendanceTabAccess: ClassAccess;
    restrictSessionsRecordingsByGroups: boolean;
    google: boolean;
    msOffice: boolean;
    aiTa: boolean;
    classAccess: ClassAccess;
    allowInstructorSwap: boolean;
    enableBreakoutRoomCloudRecording: boolean;
    preventLeaveSessionRedirect: boolean;
    enableBreakoutRoomSecurity: boolean;
    whiteboardScreenshotFeature: boolean;
    instructorChatDownload: boolean;
    isBbData: boolean;
    dataPortalAdmin: boolean;
    dataPortalBusinessAnalyst: boolean;
    dataPortalClassBusinessAnalyst: boolean;
    dataPortalCustomerSupport: boolean;
    dataPortalInstructor: boolean;
    dataPortalStudent: boolean;
    allowAiForSupport: boolean;
    allowAiForInsights: boolean;
    options: SchoolSettingsOptions;
}

export interface AuthSettings {
    vanityUrl: string;
    domains: string[];
    joinMeetingButton: boolean;
    allowPMI: boolean;
    forcedAuthentication: boolean;
    autoVerify: 'never' | 'optional' | 'always';
    unauthorizedUserAccess: UnauthorizedUserAccess;
    unauthorizedUserText: string | null;
}

export interface RecordingSettings {
    autoSave: boolean;
    cloudSave: boolean;
    localSave: boolean;
    participantsCanDownload: boolean;
}

export interface ClassForTeamsSettings {
    azureTenantIds?: string[];
}

export interface OrgSettings {
    organizationType: OrganizationType;
    terminologySettings: TerminologySettings;
    featureSettings: FeatureSettings;
    recordingSettings: RecordingSettings;
    authSettings: AuthSettings;
    classForTeamsSettings: ClassForTeamsSettings;
    zoomAppId: ZoomAppId;
}

export interface DefaultSettings {
    academic: OrgSettings;
    corporate: OrgSettings;
}
